import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  cn,
  DateTimePicker,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@crossnokaye/ux-common';

import {
  ExtendShutdownFormData,
  ExtendShutdownFormSchema,
} from '@/components/shutdown-banner/schema';
import { ShutdownDialog } from '@/components/shutdown-banner/shutdown-dialog';
import { useShutdownActionPermission } from '@/components/shutdown-banner/use-shutdown-action-permission';
import { createFacilityDate } from '@/helpers/datetime/facility-date';
import { formatUTCISO } from '@/helpers/datetime/utc-date';
import { useShutdownApp } from '@/hooks/query-hooks/scheduling';
import { usePatchScheduledAppSettings } from '@/hooks/query-hooks/settings';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { timezoneAtom } from '@/state/time.state';

export const ExtendShutdownAction: React.FC<{ endTime: Date | undefined }> = ({ endTime }) => {
  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { data: shutdownApp } = useShutdownApp({
    orgId,
    agentId,
  });

  const { mutate: extendShutdown } = usePatchScheduledAppSettings({
    orgId,
    agentId,
    appId: shutdownApp?.[0]?.appId,
  });

  const timeZone = useAtomValue(timezoneAtom);
  const { isDisabled, tooltipText } = useShutdownActionPermission();

  const form = useForm<ExtendShutdownFormData>({
    resolver: zodResolver(ExtendShutdownFormSchema),
    defaultValues: {
      extendShutdownDateTime: createFacilityDate(),
      restartDateTime: endTime ? createFacilityDate(endTime) : createFacilityDate(),
    },
    mode: 'onChange',
  });

  // Validate initial value
  const watch = form.watch('extendShutdownDateTime');
  useEffect(() => {
    if (watch) {
      form.trigger('extendShutdownDateTime');
    }
  }, [watch, form]);

  const onSubmit: SubmitHandler<ExtendShutdownFormData> = async (data) => {
    await extendShutdown([
      {
        name: 'extend_shutdown_until',
        value: JSON.stringify(formatUTCISO(data.extendShutdownDateTime)),
      },
    ]);
  };

  return (
    <ShutdownDialog
      id={'extend'}
      form={form}
      isDisabled={isDisabled}
      onSubmit={onSubmit}
      title={'Are you sure you want to extend the current Shutdown?'}
      description={'Enter a date and time to restart the facility.'}
      confirmText={form.formState.isSubmitting ? 'Extending...' : 'Yes, Extend Shutdown'}
      cancelText={'Do Not Extend Shutdown'}
      tooltipText={tooltipText}
      triggerText={'Extend Shutdown'}
    >
      <FormField
        control={form.control}
        name={'extendShutdownDateTime'}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <DateTimePicker
                className={cn(!field.value && 'text-muted-foreground font-normal')}
                date={field.value ? createFacilityDate(field.value) : createFacilityDate()}
                data-testid={'extend-shutdown-date-time-picker'}
                disabledDates={(date: Date) =>
                  date.setHours(0, 0, 0, 0) < createFacilityDate().setHours(0, 0, 0, 0)
                }
                placeholderText={'Select date and time'}
                popoverContentClassName={'z-[calc(var(--z-modal)+1)]'} // Content z-index + 1 to ensure it is above the Dialog
                setDate={(date) => field.onChange(date)}
                timePeriodSelectContentClassName={'z-[calc(var(--z-modal)+2)]'} // Time period select z-index + 2 to ensure it is above the Content
                timeZone={timeZone}
                variant={'secondary'}
              />
            </FormControl>
            <FormMessage className={'pl-2'} />
          </FormItem>
        )}
      />
    </ShutdownDialog>
  );
};
